import { MessagingNotificationFilterTypes } from "@avenueops/frontend-shared";
import { ClientIntegration, ManualNotificationType } from "@avenueops/shared-types";
import { SmartForm, Button } from "@avenueops/web-components";
import { ArrowPathIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import { NotificationType } from "@prisma/client";
import HintIconWithTooltip from "components/HintIconWithTooltip";
import CustomNotificationsAndSignals from "components/monitors/editFormReboot/CustomNotificationsAndSignals";
import React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { trpc2 } from "utils/trpc";
import validateNotificationInput from "utils/validateNotificationInput";

interface Props {
  disableCustomize?: boolean;
  notificationPrefix: string;

  slackIntegrations?: ClientIntegration[];
}

export function SlackNotificationInput({ disableCustomize, notificationPrefix, slackIntegrations }: Props): JSX.Element {
  const { setError, watch } = useFormContext();
  const notification: ManualNotificationType = useWatch({ name: notificationPrefix });
  const selectedIntegrationId = watch(`${notificationPrefix}.integrationId`);

  const [hardRefresh, setHardRefresh] = React.useState<boolean>(false);

  const {
    data: slackRecipientOptions,
    isLoading: slackRecipientsLoading,
    refetch,
  } = trpc2.v2.slack.getRecipients.useQuery({
    integrationId: selectedIntegrationId,
    hardRefresh: hardRefresh,
  });

  return (
    <>
      <div className="col-span-4">
        <SmartForm.DropdownSelect
          defaultValue={slackIntegrations && slackIntegrations.length === 1 ? slackIntegrations[0].integrationId : undefined}
          name={`${notificationPrefix}.integrationId`}
          title="Slack Workspace"
          options={
            slackIntegrations?.map((integration) => ({
              label: integration.name ?? "Notion workspace",
              value: integration.integrationId,
            })) ?? []
          }
        />
        <div className="flex w-full gap-1">
          <label htmlFor={`${notificationPrefix}.recipientValue`} className="font-medium text-sm text-gray-950">
            Slack Channel, Member ID or Dynamic Input
          </label>
          <div>
            <HintIconWithTooltip
              LeadIcon={InformationCircleIcon}
              tooltipText="To DM Avenue users integrated with Slack dynamically, use {{lookup email}}. To DM the assignee, use {{lookup metadata.assignee.email}}"
            />
          </div>
        </div>
        {slackRecipientOptions ? (
          <div className="flex flex-col gap-2">
            <SmartForm.DropdownSelect
              name={`${notificationPrefix}.recipientValue`}
              options={slackRecipientOptions}
              placeholder="#general, {{channel_id}}, @U123ABC (Slack member ID)"
              onCreate={(searchValue) => {
                const valid = validateNotificationInput(searchValue, NotificationType.SLACK);
                if (!valid) {
                  setError(`${notificationPrefix}.recipientValue`, { type: "custom", message: "Invalid Slack channel or username format" });
                  return;
                }
                return { label: searchValue, value: searchValue };
              }}
            />
            <div className="text-xs text-gray-500">
              Can't find the channel you're looking for? Click the refresh button. Make sure you have invited the Avenue App by typing '@Avenue' in the message
              composer for private channels.
            </div>
            <Button
              onClick={async () => {
                setHardRefresh(true);
                await refetch();
                setHardRefresh(false);
              }}
              LeadIcon={ArrowPathIcon}
              size="SMALL"
              type="TEXT"
            >
              Refresh
            </Button>
          </div>
        ) : (
          <SmartForm.TextInput name={`${notificationPrefix}.recipientValue`} placeholder="#general, {{channel_id}}, @U123ABC (Slack member ID)" />
        )}
      </div>
      {disableCustomize ? null : (
        <div className="col-span-6">
          <CustomNotificationsAndSignals
            notificationPrefix={notificationPrefix}
            notificationType={notification.manualType}
            supportedTypes={MessagingNotificationFilterTypes}
          />
        </div>
      )}
    </>
  );
}
